/**
 * ArubaFirmwareUpdatePage — pick a target group + firmware version, then push.
 *
 * Mirrors the legacy Django page's "select group → execute → tabs" flow but
 * with a tighter UI: group picker on the left, firmware picker grid in the
 * middle, and a destructive Push CTA in the action bar.
 */
import { useEffect, useMemo, useReducer, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  PlayCircle,
  ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardSub, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { FormSkeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { ArubaSubNav } from "@/components/app/aruba-sub-nav";
import { NoVendorKeysBanner } from "@/components/app/no-vendor-keys-banner";
import { useArubaGroups } from "@/stores/aruba-groups";
import type { ArubaGroup, ArubaFirmware } from "@/api/schemas/aruba";
import { MOCK_ARUBA_FIRMWARE } from "@/api/fixtures/aruba-firmware";
import { cn } from "@/lib/cn";
import { useUiAutoAnimate } from "@/components/ui/auto-animate";

export function ArubaFirmwareUpdatePage() {
  const animatedRootRef = useUiAutoAnimate();

  const {
    data: groups,
    error,
    status: groupsStatus,
    load: loadGroups,
  } = useArubaGroups();
  useEffect(() => {
    void loadGroups();
  }, [loadGroups]);

  const [group, setGroup] = useState<ArubaGroup | null>(null);
  const [firmware, setFirmware] = useState<ArubaFirmware | null>(null);

  type PushState = { confirmOpen: boolean; pushOpen: boolean; pushPct: number | undefined; pushDone: boolean };
  const [{ confirmOpen, pushOpen, pushPct, pushDone }, setPush] = useReducer(
    (s: PushState, p: Partial<PushState>) => ({ ...s, ...p }),
    { confirmOpen: false, pushOpen: false, pushPct: undefined, pushDone: false },
  );

  // Filter the firmware list to ones that apply to the selected group's
  // allowed device types. Empty group → show everything.
  const applicableFirmware = useMemo(() => {
    if (!group) return MOCK_ARUBA_FIRMWARE;
    const allowed = new Set(group.allowedDevices ?? []);
    return MOCK_ARUBA_FIRMWARE.filter((fw) =>
      fw.appliesTo.some((dev) => {
        // Group allowedDevices store types like "SW" / "AP"; firmware
        // appliesTo lists models like "CX-6300" / "AP-655". Map by prefix.
        if (allowed.has("SW") && dev.startsWith("CX-")) return true;
        if (allowed.has("AP") && dev.startsWith("AP-")) return true;
        return allowed.size === 0; // mixed groups see everything
      }),
    );
  }, [group]);

  function startPush() {
    if (!group || !firmware) return;
    setPush({ confirmOpen: false, pushOpen: true, pushDone: false, pushPct: 0 });
    // Simulate progress — replace with real celery polling once the
    // /api/aruba/firmware/push/ endpoint exists.
    let pct = 0;
    const t = setInterval(() => {
      pct = Math.min(100, pct + Math.random() * 18);
      setPush({ pushPct: pct });
      if (pct >= 100) {
        clearInterval(t);
        setTimeout(() => {
          setPush({ pushDone: true });
          setTimeout(() => setPush({ pushOpen: false }), 1200);
          toast.success(`Pushed ${firmware.version} to ${group.name}`);
        }, 300);
      }
    }, 350);
  }

  return (
    <div ref={animatedRootRef} className="flex flex-col gap-6">
      <div className="flex items-center gap-2 text-xs text-fg-mute">
        <Link to="/" className="text-fg-dim hover:text-fg">
          Operations
        </Link>
        <span className="opacity-40">/</span>
        <Link to="/aruba/keys" className="text-fg-dim hover:text-fg">
          Aruba
        </Link>
        <span className="opacity-40">/</span>
        <span className="text-fg">Firmware update</span>
      </div>

      <ArubaSubNav />

      <NoVendorKeysBanner vendor="aruba" />

      <div className="flex items-end justify-between gap-6">
        <div>
          <h1 className="m-0 text-3xl font-extrabold tracking-tight text-aurora">
            Firmware update
          </h1>
          <p className="mt-1.5 max-w-[680px] text-sm text-fg-dim">
            Pick a group, choose a GuestTek-blessed firmware version, and push
            it to every device in scope. Pushes are staged through Aruba Central
            — devices reboot in batches to keep the network up.
          </p>
        </div>
        <Badge variant="warn" size="md">
          <ShieldAlert size={11} />
          Destructive — devices will reboot
        </Badge>
      </div>

      {error ? (
        <output
          className="flex items-center gap-2.5 rounded-xl border border-peach/30 bg-peach/10 px-3.5 py-2.5 text-sm text-peach"
        >
          <AlertTriangle size={16} />
          <span>
            <b className="font-semibold">Showing fixture data.</b>{" "}
            <span className="text-peach/80">{error}</span>
          </span>
        </output>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[360px_1fr]">
        {/* Group picker — radio-card list. */}
        <Card>
          <CardContent className="flex flex-col gap-3">
            <div>
              <CardTitle>Target group</CardTitle>
              <CardSub>Pick which Aruba group receives the push.</CardSub>
            </div>
            {groupsStatus === "loading" && groups.length === 0 ? (
              <FormSkeleton lines={3} />
            ) : (
              <div className="flex flex-col gap-2">
                {groups.map((g) => {
                  const isSelected = group?.name === g.name;
                  return (
                    <label
                      key={g.name}
                      htmlFor={`grp-${g.name}`}
                      className={cn(
                        "flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition-colors",
                        isSelected
                          ? "border-violet/50 bg-aurora-soft"
                          : "border-line bg-surface hover:bg-surface-2",
                      )}
                    >
                      <input
                        type="radio"
                        id={`grp-${g.name}`}
                        name="aruba-group"
                        checked={isSelected}
                        onChange={() => {
                          setGroup(g);
                          setFirmware(null);
                        }}
                        className="mt-1.5 accent-violet"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="truncate text-sm font-semibold text-fg">
                            {g.name}
                          </div>
                          {g.compliant === false ? (
                            <Badge variant="warn" size="sm">
                              drifted
                            </Badge>
                          ) : null}
                        </div>
                        <div className="mt-0.5 text-[11px] text-fg-mute">
                          <b className="font-mono text-fg">
                            {g.deviceCount ?? 0}
                          </b>{" "}
                          dev · {g.architecture ?? "—"} · {g.aosVersion ?? "—"}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Firmware picker grid */}
        <Card>
          <CardContent className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Firmware version</CardTitle>
                <CardSub>
                  {group
                    ? `Versions applicable to ${group.name}`
                    : "Select a group above to filter applicable versions."}
                </CardSub>
              </div>
              <Button
                disabled={!group || !firmware}
                onClick={() => setPush({ confirmOpen: true })}
              >
                <PlayCircle size={14} />
                Stage push
              </Button>
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {applicableFirmware.map((fw) => {
                const isSelected = firmware?.version === fw.version;
                return (
                  <button
                    key={fw.version}
                    type="button"
                    onClick={() => setFirmware(fw)}
                    className={cn(
                      "flex flex-col items-start gap-2 rounded-xl border p-3 text-left transition-colors",
                      isSelected
                        ? "border-violet/50 bg-aurora-soft"
                        : "border-line bg-surface hover:bg-surface-2",
                    )}
                  >
                    <div className="flex w-full items-center justify-between gap-2">
                      <div className="font-mono text-sm font-bold text-fg">
                        {fw.version}
                      </div>
                      {fw.recommended ? (
                        <Badge variant="ok" size="sm">
                          <CheckCircle2 size={10} />
                          recommended
                        </Badge>
                      ) : (
                        <Badge variant="warn" size="sm">
                          not blessed
                        </Badge>
                      )}
                    </div>
                    <div className="font-mono text-[10.5px] text-fg-mute">
                      released {fw.releasedAt} · applies to{" "}
                      {fw.appliesTo.join(", ")}
                    </div>
                    {fw.highlights && fw.highlights.length > 0 ? (
                      <ul className="m-0 mt-1 flex list-none flex-col gap-1 p-0 text-xs text-fg-dim">
                        {fw.highlights.map((h) => (
                          <li key={h}>· {h}</li>
                        ))}
                      </ul>
                    ) : null}
                  </button>
                );
              })}
            </div>

            {applicableFirmware.length === 0 ? (
              <div className="rounded-xl border border-line bg-surface p-5 text-center text-sm text-fg-mute">
                No firmware versions match the selected group's device types.
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>

      <FirmwarePushDialogs
        confirmOpen={confirmOpen}
        setConfirmOpen={(v) => setPush({ confirmOpen: v })}
        pushOpen={pushOpen}
        setPushOpen={(v) => setPush({ pushOpen: v })}
        pushDone={pushDone}
        pushPct={pushPct}
        firmware={firmware}
        group={group}
        startPush={startPush}
      />
    </div>
  );
}

function FirmwarePushDialogs({
  confirmOpen,
  setConfirmOpen,
  pushOpen,
  setPushOpen,
  pushDone,
  pushPct,
  firmware,
  group,
  startPush,
}: {
  confirmOpen: boolean;
  setConfirmOpen: (v: boolean) => void;
  pushOpen: boolean;
  setPushOpen: (v: boolean) => void;
  pushDone: boolean;
  pushPct: number | undefined;
  firmware: ArubaFirmware | null;
  group: ArubaGroup | null;
  startPush: () => void;
}) {
  return (
    <>
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogTitle>Push firmware?</DialogTitle>
          <DialogDescription className="mt-2">
            <span>About to push:</span>
            <span className="mt-2 block rounded-lg border border-line bg-surface px-3 py-2 font-mono text-sm text-fg">
              {firmware?.version}
            </span>
            <span className="mt-2 block">to:</span>
            <span className="mt-2 block rounded-lg border border-line bg-surface px-3 py-2 font-mono text-sm text-fg">
              {group?.name} · {group?.deviceCount ?? 0} device
              {(group?.deviceCount ?? 0) === 1 ? "" : "s"}
            </span>
            <span className="mt-3 block">
              Devices reboot in batches; expect ~5 minutes per batch. Pushing a
              non-recommended version is allowed but flagged for compliance audit.
            </span>
          </DialogDescription>
          <div className="mt-5 flex justify-end gap-2.5">
            <DialogClose asChild>
              <Button variant="ghost">Cancel</Button>
            </DialogClose>
            <Button onClick={startPush}>
              <ArrowRight size={14} />
              Push now
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={pushOpen} onOpenChange={setPushOpen}>
        <DialogContent>
          {pushDone ? (
            <div className="flex flex-col items-start gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-mint/15 text-mint">
                <CheckCircle2 size={22} />
              </div>
              <DialogTitle>Push complete</DialogTitle>
              <DialogDescription>
                {firmware?.version} applied to {group?.name}.
              </DialogDescription>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <DialogTitle>
                Pushing {firmware?.version} → {group?.name}
              </DialogTitle>
              <DialogDescription>
                Devices are rebooting in batches…
              </DialogDescription>
              <Progress
                value={pushPct}
                hint={`${Math.round(pushPct ?? 0)}% applied`}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
